import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Paper } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useRef } from "react";
import { TrendingDataByTagId } from "@/pages/caster/data-entry/models/caster.models";
import { useTheme } from "@mui/material/styles";
import { dataEntryTableStyles } from "../styles/dataEntryTableStyles";

interface ProcessedTrendingData
  extends Omit<TrendingDataByTagId, "TagId" | "Datetime"> {
  TagId: string;
  Datetime: string;
}

interface MultiLineChartProps {
  title: string;
  data: TrendingDataByTagId[];
  valueUnit?: string;
}

interface EChartsTooltipParam {
  value: [number, number];
  seriesName: string;
  color: string;
  dataIndex: number;
  componentType: string;
  componentSubType: string;
}

const colors = ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd"];

export const MultiLineChart = ({
  title,
  data,
  valueUnit = "IPM",
}: MultiLineChartProps) => {
  const theme = useTheme();
  const styles = dataEntryTableStyles(theme);
  const echartsRef = useRef<ReactECharts>(null);

  const getChartOptions = useMemo(() => {
    const processedData: ProcessedTrendingData[] =
      data
        ?.filter(
          (item): item is TrendingDataByTagId =>
            item !== null &&
            item.Datetime !== null &&
            item.TagId !== null &&
            item.TagName !== null,
        )
        .map((temp) => ({
          ...temp,
          TagId: temp.TagId?.toString() || "",
          Datetime:
            temp.Datetime instanceof Date
              ? dxtToLocalServerTime(temp.Datetime, "yyyy-MM-dd HH:mm:ss")
              : (temp.Datetime as Date | null)?.toString() || "",
          Value: temp.Value ?? 0,
        }))
        .filter((item) => item.Datetime && item.TagId && item.TagName) || [];

    if (processedData.length === 0) {
      return {};
    }

    const values = processedData
      .map((item) => item.Value)
      .filter((value): value is number => value !== null);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    const margin = Math.ceil((maxValue - minValue) * 0.05);
    const yMin = Math.max(0, Math.floor(minValue) - margin);
    const yMax = Math.ceil(maxValue + margin);

    const interval = Math.ceil((yMax - yMin) / 5);

    const groupedData = processedData.reduce<
      Record<string, ProcessedTrendingData[]>
    >((acc, item) => {
      if (!acc[item.TagId]) {
        acc[item.TagId] = [];
      }
      acc[item.TagId].push(item);
      return acc;
    }, {});

    const tagColorMap: Record<string, string> = {};
    Object.keys(groupedData).forEach((tagId, index) => {
      tagColorMap[tagId] = colors[index % colors.length];
    });

    return {
      animation: false,
      toolbox: {
        feature: {
          saveAsImage: {},
        },
      },
      dataZoom: [
        {
          type: "inside",
          show: true,
        },
      ],
      tooltip: {
        trigger: "axis",
        formatter: (params: EChartsTooltipParam[]) => {
          if (!params || params.length === 0) return "";

          const firstParam = params[0];
          const datetime = new Date(firstParam.value[0]).toLocaleTimeString(
            "en-US",
            {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            },
          );

          const tooltipContent = params
            .map(
              (param) =>
                `${param.seriesName}: ${param.value[1].toFixed(1)} ${valueUnit}`,
            )
            .join("<br>");

          return `Time: ${datetime}<br>${tooltipContent}`;
        },
      },
      legend: {
        data: Object.keys(groupedData).map(
          (tagId) => groupedData[tagId][0].TagName || "",
        ),
        top: "bottom",
        selectedMode: true,
      },
      xAxis: {
        type: "time",
        axisLabel: {
          formatter: (value: number) => {
            return new Date(value).toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
            });
          },
        },
      },
      yAxis: {
        type: "value",
        name: valueUnit,
        nameTextStyle: {
          fontSize: 14,
          align: "left",
        },
        nameLocation: "end",
        min: yMin,
        max: yMax,
        interval: interval,
      },
      series: Object.entries(groupedData).map(([tagId, tagData]) => ({
        name: tagData[0].TagName || "",
        type: "line",
        smooth: true,
        symbol: "none",
        sampling: "lttb",
        itemStyle: {
          color: tagColorMap[tagId],
        },
        data: tagData.map((item) => [
          new Date(item.Datetime).getTime(),
          item.Value,
        ]),
      })),
    };
  }, [data]);

  useEffect(() => {
    if (echartsRef.current) {
      const instance = echartsRef.current.getEchartsInstance();
      instance.setOption(getChartOptions, {
        replaceMerge: ["series"],
        lazyUpdate: true,
      });
    }
  }, [getChartOptions]);

  return (
    <Paper>
      <Box padding={1} sx={styles.agGridBox}>
        <Box>
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.common.white,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              padding: (theme) => theme.spacing(1),
              paddingLeft: (theme) => theme.spacing(3),
              fontWeight: 600,
              textAlign: "center",
              fontSize: "12px",
              color: (theme) => theme.palette.text.primary,
              fontFamily:
                '"Open Sans", "Lucida Grande", Arial, Verdana, Helvetica, sans-serif',
            }}
          >
            {title}
          </Box>
          <ReactECharts
            ref={echartsRef}
            option={getChartOptions}
            notMerge={false}
          />
        </Box>
      </Box>
    </Paper>
  );
};
